import { useState, type ReactNode } from 'react';
import { useTransitionCounter } from './useTransitionCounter';
import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  usePrefersReducedMotion,
  VStack,
} from '@chakra-ui/react';
import { Eye, Pencil, RefreshCw } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { toast } from '@duke-hq/app-admin-web/components/ToastContainer';
import { TranslationStatusChip, NotOfferedChip, isTranslatedStatus } from './TranslationStatusChip';
import { generationDetailsLabel } from './TranslationGenerationDetails';
import { RetranslateCellDialog } from './RetranslateDialogs';
import { LanguageStatusRow, languageListTemplateColumns } from './LanguageStatusRow';
import { isArrived } from './useStatusTransition';
import { translationsMotion } from './translationsMotion';
import type {
  LanguageTranslation,
  TranslatableResourceKind,
  useContentTranslations,
} from './useContentTranslations';
import { testIds } from './testIds';

const { durations, easings } = translationsMotion;

// Matches the published badge and version history so timestamps read the same.
const DATE_FORMAT = 'D MMM YYYY, h:mma';

type ContentTranslations = ReturnType<typeof useContentTranslations>;

export function TranslationsPanel({
  resourceVersionId,
  resourceKind,
  publishedVersion,
  translations,
  onViewLanguage,
  viewLabel,
  onEditLanguage,
  isEditDisabled = false,
}: {
  resourceVersionId: number;
  resourceKind: TranslatableResourceKind;
  publishedVersion: number | null | undefined;
  translations: ContentTranslations;
  onViewLanguage?: (language: LanguageTranslation) => void;
  viewLabel?: string;
  onEditLanguage?: (language: LanguageTranslation) => void;
  isEditDisabled?: boolean;
}) {
  const { isLoading, isError, refetch, languages, lastUpdatedAt } = translations;
  const utils = trpcAdmin.useUtils();
  const [retranslateTarget, setRetranslateTarget] = useState<LanguageTranslation | null>(null);

  // Shared by retry and re-translate: flip the row to a queued read (startedAt
  // cleared so it says "Queued", not "Generating"); the invalidate reconciles.
  const setRowPending = (language: string) => {
    utils.contentTranslations.listByResourceVersion.setData(
      { resourceVersionId, resourceKind },
      (previous) =>
        previous == null
          ? previous
          : {
              ...previous,
              rows: previous.rows.map((row) =>
                row.language === language ? { ...row, status: 'PENDING', startedAt: null } : row,
              ),
            },
    );
    void utils.contentTranslations.listByResourceVersion.invalidate({
      resourceVersionId,
      resourceKind,
    });
  };

  const retranslate = trpcAdmin.contentTranslations.retranslate.useMutation({
    onSuccess: (data, variables) => {
      setRetranslateTarget(null);
      if (!data.enqueued) {
        toast({
          status: 'warning',
          title: t`Couldn't start this re-translation`,
          description: t`Couldn't start re-translating this language. Try again in a moment.`,
        });
        return;
      }
      setRowPending(variables.language);
    },
    onError: () => {
      toast({
        status: 'error',
        title: t`Could not re-translate`,
        description: t`Couldn't start re-translating this language. Try again in a moment.`,
      });
    },
  });
  const retry = trpcAdmin.contentTranslations.retry.useMutation({
    onSuccess: (data, variables) => {
      if (!data.enqueued) {
        toast({
          status: 'warning',
          title: t`Couldn't start this translation`,
          description: t`Couldn't start translating this language. Try again in a moment.`,
        });
        return;
      }
      setRowPending(variables.language);
    },
    onError: () => {
      toast({
        status: 'error',
        title: t`Could not retry translation`,
        description: t`Couldn't start translating this language. Try again in a moment.`,
      });
    },
  });

  return (
    <>
      <TranslationsPanelView
        languages={languages}
        publishedVersion={publishedVersion}
        lastUpdatedAt={lastUpdatedAt}
        isLoading={isLoading}
        isError={isError}
        onRetryLoad={() => void refetch()}
        onRetry={(language) =>
          retry.mutate({ resourceVersionId, resourceKind, language: language.code })
        }
        retryingLanguageCode={retry.isPending ? retry.variables?.language : undefined}
        onViewLanguage={onViewLanguage}
        viewLabel={viewLabel}
        onEditLanguage={onEditLanguage}
        isEditDisabled={isEditDisabled}
        onRetranslateLanguage={(language) => setRetranslateTarget(language)}
      />
      <RetranslateCellDialog
        isOpen={retranslateTarget != null}
        onClose={() => setRetranslateTarget(null)}
        isLoading={retranslate.isPending}
        languageName={retranslateTarget?.englishName ?? ''}
        segmentCount={retranslateTarget?.row?.totalSegmentCount ?? null}
        onConfirm={() => {
          if (retranslateTarget != null) {
            retranslate.mutate({
              resourceVersionId,
              resourceKind,
              language: retranslateTarget.code,
            });
          }
        }}
      />
    </>
  );
}

// Pure (no tRPC) so it renders in Storybook without a provider.
export function TranslationsPanelView({
  languages,
  publishedVersion,
  lastUpdatedAt,
  isLoading = false,
  isError = false,
  onRetryLoad,
  onRetry,
  retryingLanguageCode,
  onViewLanguage,
  viewLabel,
  onEditLanguage,
  isEditDisabled = false,
  onRetranslateLanguage,
}: {
  languages: LanguageTranslation[];
  publishedVersion: number | null | undefined;
  lastUpdatedAt: Date | null | undefined;
  isLoading?: boolean;
  isError?: boolean;
  onRetryLoad?: () => void;
  onRetry?: (language: LanguageTranslation) => void;
  retryingLanguageCode?: string;
  onViewLanguage?: (language: LanguageTranslation) => void;
  viewLabel?: string;
  onEditLanguage?: (language: LanguageTranslation) => void;
  isEditDisabled?: boolean;
  onRetranslateLanguage?: (language: LanguageTranslation) => void;
}) {
  return (
    <Box data-testid={testIds.panel}>
      <Box px={3} py={2} borderBottomWidth="1px" borderColor="border.default">
        {publishedVersion != null ? (
          <Text fontSize="xs" fontWeight="medium">
            <Trans>Version {publishedVersion}</Trans>
          </Text>
        ) : null}
        <Text fontSize="xs" color="fg.muted">
          <Trans>Generated when you publish.</Trans>
        </Text>
      </Box>

      {isError ? (
        <VStack align="stretch" spacing={2} px={3} py={4}>
          <Text fontSize="sm" color="fg.muted">
            <Trans>Couldn't load translation status</Trans>
          </Text>
          <Button
            size="xs"
            variant="secondary"
            alignSelf="start"
            onClick={() => onRetryLoad?.()}
            data-testid={testIds.retryLoadButton}
          >
            <Trans>Retry</Trans>
          </Button>
        </VStack>
      ) : isLoading ? (
        <Flex justify="center" py={4}>
          <Spinner size="sm" />
        </Flex>
      ) : (
        <Grid
          templateColumns={languageListTemplateColumns()}
          columnGap={3}
          maxH={pxToRem(520)}
          overflowY="auto"
        >
          {languages.map((language, index) => (
            <LanguageRowView
              key={language.code}
              language={language}
              breathIndex={index}
              onRetry={onRetry == null ? undefined : () => onRetry(language)}
              isRetrying={retryingLanguageCode === language.code}
              onView={onViewLanguage == null ? undefined : () => onViewLanguage(language)}
              viewLabel={viewLabel}
              onEdit={onEditLanguage == null ? undefined : () => onEditLanguage(language)}
              isEditDisabled={isEditDisabled}
              onRetranslate={
                onRetranslateLanguage == null ? undefined : () => onRetranslateLanguage(language)
              }
            />
          ))}
        </Grid>
      )}

      {!isError && lastUpdatedAt != null ? (
        <Box px={3} py={2} borderTopWidth="1px" borderColor="border.default">
          <Text fontSize="xs" color="fg.muted">
            <Trans>Last updated {djs(lastUpdatedAt).format(DATE_FORMAT)}</Trans>
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

function isArrival(
  previous: LanguageTranslation['status'] | null,
  next: LanguageTranslation['status'],
): boolean {
  const wasInFlight = previous === 'queued' || previous === 'generating';
  return wasInFlight && isArrived(next);
}

// Something exists to view/edit: available, partially available, or stale.
function isViewable(status: LanguageTranslation['status']): boolean {
  return isTranslatedStatus(status) || status === 'stale';
}

function RowActionIcon({
  label,
  icon,
  onClick,
  isDisabled = false,
  isLoading = false,
  'data-testid': testId,
}: {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  'data-testid': string;
}) {
  // A disabled IconButton emits no pointer events, so the tooltip is wrapped
  // around a Box that always does, keeping the "why is this disabled" hint.
  return (
    <Tooltip label={label} openDelay={300}>
      <Box as="span" display="inline-flex">
        <IconButton
          size="xs"
          variant="ghost"
          aria-label={label}
          isDisabled={isDisabled}
          isLoading={isLoading}
          onClick={onClick}
          data-testid={testId}
        >
          {icon}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

export function LanguageRowView({
  language,
  breathIndex,
  onRetry,
  isRetrying = false,
  onView,
  viewLabel,
  onEdit,
  isEditDisabled = false,
  onRetranslate,
}: {
  language: LanguageTranslation;
  breathIndex: number;
  onRetry?: () => void;
  isRetrying?: boolean;
  onView?: () => void;
  viewLabel?: string;
  onEdit?: () => void;
  isEditDisabled?: boolean;
  onRetranslate?: () => void;
}) {
  const motionDisabled = usePrefersReducedMotion();
  const washKey = useTransitionCounter(language.status, isArrival, !motionDisabled);
  const viewable = isViewable(language.status);

  // null, not an empty fragment: the badge fallback below keys off actions != null.
  const rowActions = (): ReactNode => {
    if (!language.isOffered) {
      return null;
    }
    if (language.status === 'failed' && (onEdit != null || onRetry != null)) {
      return (
        <>
          {onEdit != null ? (
            <RowActionIcon
              label={t`Edit translated content`}
              icon={<Pencil size={iconSize.sm} />}
              onClick={() => onEdit()}
              data-testid={testIds.editButton}
            />
          ) : null}
          {onRetry != null ? (
            <RowActionIcon
              label={t`Retry translation`}
              icon={<RefreshCw size={iconSize.sm} />}
              isLoading={isRetrying}
              onClick={() => onRetry()}
              data-testid={testIds.retryButton}
            />
          ) : null}
        </>
      );
    }
    if (viewable && (onView != null || onEdit != null || isEditDisabled)) {
      return (
        <>
          {onView != null ? (
            <RowActionIcon
              label={viewLabel ?? t`View translation on the canvas`}
              icon={<Eye size={iconSize.sm} />}
              onClick={() => onView()}
              data-testid={testIds.viewButton}
            />
          ) : null}
          {onEdit != null || isEditDisabled ? (
            <RowActionIcon
              label={t`Edit translated content`}
              icon={<Pencil size={iconSize.sm} />}
              isDisabled={isEditDisabled}
              onClick={onEdit == null ? undefined : () => onEdit()}
              data-testid={testIds.editButton}
            />
          ) : null}
          {onRetranslate != null ? (
            <RowActionIcon
              label={t`Re-translate with the current engine`}
              icon={<RefreshCw size={iconSize.sm} />}
              onClick={() => onRetranslate()}
              data-testid={testIds.retranslateButton}
            />
          ) : null}
        </>
      );
    }
    return null;
  };
  const actions = rowActions();

  return (
    <LanguageStatusRow
      code={language.code}
      endonym={language.endonym}
      englishName={language.englishName}
      muted={!language.isOffered}
      // On the name, not the chip: a fully translated row shows action icons
      // instead of a chip, so the name is the one hover target every row has.
      nameTooltip={language.isOffered ? generationDetailsLabel(language.row) : null}
      rowProps={{
        position: 'relative',
        px: 3,
        py: 2,
        borderBottomWidth: '1px',
        borderColor: 'border.default',
        _last: { borderBottomWidth: 0 },
        _hover: { bg: 'bg.subtle' },
        'data-testid': testIds.panelRow,
      }}
      overlay={
        washKey > 0 ? (
          <Box
            key={washKey}
            position="absolute"
            inset={0}
            pointerEvents="none"
            aria-hidden
            sx={{
              animation: `translationRowWash ${durations.wash}ms ${easings.standard}`,
              '@keyframes translationRowWash': {
                '0%': { backgroundColor: 'transparent' },
                '40%': { backgroundColor: 'var(--chakra-colors-green-100)' },
                '100%': { backgroundColor: 'transparent' },
              },
            }}
          />
        ) : null
      }
      status={
        !language.isOffered ? (
          <NotOfferedChip />
        ) : isTranslatedStatus(language.status) && actions != null ? null : (
          <TranslationStatusChip status={language.status} breathIndex={breathIndex} />
        )
      }
      actions={actions}
    />
  );
}
